/**
 * @remarks RuntimeDocumentV1 基础合同、克隆与 Store 校验
 * @module @yimoka/designable-playground/runtime
 */

import { UNSAFE_SCHEMA_OBJECT_KEYS } from '../shared/schema';

import type {
  RuntimeBlockingErrorCode,
  RuntimeDocumentV1,
  RuntimeJsonObject,
  RuntimeJsonValue,
} from './contracts';

export const RUNTIME_DOCUMENT_KIND = 'yimoka.designable.runtime-document';
export const RUNTIME_DOCUMENT_VERSION = 1;
export const RUNTIME_SCHEMA_FIDELITY_PROP = '$runtimeSchemaFidelityV1';

export const RUNTIME_DOCUMENT_KEYS = new Set([
  'entityConfig',
  'kind',
  'metadata',
  'schema',
  'scope',
  'store',
  'version',
]);

export const RUNTIME_DOCUMENT_SIDECAR_KEYS = new Set([
  'entityConfig',
  'metadata',
  'scope',
  'store',
]);

export const RUNTIME_SCOPE_RESERVED_KEYS = new Set([
  '$biz',
  '$config',
  '$entity',
  '$root',
  '$store',
  'components',
]);

export const RUNTIME_STORE_KEYS = new Set([
  'api',
  'defaultValues',
  'dictConfig',
  'extInfo',
  'fieldsConfig',
  'options',
  'type',
]);

export const RUNTIME_DICT_KEYS = new Set([
  'api',
  'byField',
  'childrenKey',
  'data',
  'field',
  'keys',
  'paramKeys',
  'toMap',
  'toOptions',
  'type',
]);

export const RUNTIME_ENTITY_KEYS = new Set([
  'api',
  'basePath',
  'breadcrumb',
  'defaultDetailValues',
  'defaultFormValues',
  'defaultQueryValues',
  'dictConfig',
  'extensions',
  'fieldsConfig',
  'idKey',
  'idsKey',
  'i18nFieldMap',
  'i18nInSchema',
  'i18nLangAPI',
  'i18nOutSchema',
  'labelKey',
  'map',
  'name',
  'options',
  'path',
]);

export const RUNTIME_REQUEST_KEYS = new Set([
  'data',
  'method',
  'params',
  'url',
]);

export const RUNTIME_METADATA_KEYS = new Set([
  'extensions',
  'scenarioId',
  'template',
]);

export const RUNTIME_TEMPLATE_REFERENCE_KEYS = new Set([
  'id',
  'version',
]);

export const DATA_BINDING_MANUAL_KEYS: Record<string, readonly string[]> = {
  Cascader: ['options', 'data'],
  Checkbox: ['options', 'data'],
  Mentions: ['options', 'data'],
  Radio: ['options', 'data'],
  SearchSelect: ['options', 'data'],
  Select: ['options', 'data'],
  Transfer: ['dataSource', 'data'],
  TreeSelect: ['treeData', 'data'],
};

export const RUNTIME_OPTION_SCHEMA_KEYS = ['enum', 'oneOf', 'x-enum'] as const;
export const RUNTIME_UPLOAD_NETWORK_KEYS = new Set([
  'action',
  'customRequest',
  'headers',
  'method',
  'withCredentials',
]);

export const RUNTIME_SEARCH_REQUEST_PROP_KEYS: Record<string, readonly string[]> = {
  Search: ['api'],
  SearchSelect: ['api', 'labelAPI'],
};

export const RUNTIME_NAVIGATION_PROP_KEYS: Record<string, readonly string[]> = {
  Button: ['href', 'ping'],
  Link: ['href', 'to', 'ping'],
};

export interface IRuntimeSchemaFidelityV1 {
  omitDesignableIdFor: string[];
  omitIndexFor: string[];
}

export type RuntimeDocumentSidecarV1 = Omit<RuntimeDocumentV1, 'schema'> & {
  [RUNTIME_SCHEMA_FIDELITY_PROP]?: IRuntimeSchemaFidelityV1;
};

/** Runtime 文档校验问题，路径和错误码可直接交给宿主做诊断展示。 */
export interface IRuntimeDocumentValidationIssue {
  code: RuntimeBlockingErrorCode;
  message: string;
  path: string;
  severity: 'error';
}

/** Runtime 文档校验结果；成功时 document 一定是与输入隔离的 JSON-safe 副本。 */
export interface IRuntimeDocumentValidationResult {
  document?: RuntimeDocumentV1;
  issues: IRuntimeDocumentValidationIssue[];
  valid: boolean;
}

/** Runtime 宿主 scope 校验结果；成功时 scope 是隔离的 JSON 对象副本。 */
export interface IRuntimeEnvironmentScopeValidationResult {
  issues: IRuntimeDocumentValidationIssue[];
  scope?: RuntimeJsonObject;
  valid: boolean;
}

/** Runtime 文档无法安全进入设计树时抛出的结构化错误。 */
export class RuntimeDocumentValidationError extends Error {
  readonly code: RuntimeBlockingErrorCode;

  readonly issues: IRuntimeDocumentValidationIssue[];

  /** 用全部阻断问题构造可由 Playground onError 透传的异常。 */
  constructor(issues: IRuntimeDocumentValidationIssue[]) {
    super(issues.map(issue => `${issue.path}: ${issue.message}`).join('；'));
    this.name = 'RuntimeDocumentValidationError';
    this.code = issues[0]?.code ?? 'invalid-runtime-document';
    this.issues = issues;
  }
}

/** 判断值是否为非数组对象；具体 JSON 原型约束由克隆阶段继续校验。 */
export const isObjectRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object'
  && value !== null
  && !Array.isArray(value)
);

/** 追加稳定的 Runtime 阻断问题。 */
export const pushRuntimeIssue = (
  issues: IRuntimeDocumentValidationIssue[],
  code: RuntimeBlockingErrorCode,
  path: string,
  message: string,
) => {
  // 多语言映射有更具体的公开错误合同；即使危险键在递归克隆阶段先被发现，也保持该稳定 code。
  const resolvedCode = code === 'runtime-unsafe-key'
    && path.includes('.entityConfig.i18nFieldMap.')
    ? 'invalid-i18n-field-map'
    : code;
  issues.push({
    code: resolvedCode,
    message,
    path,
    severity: 'error',
  });
};

/**
 * 递归校验并克隆 JSON 值。
 *
 * 这里不借助 JSON.stringify，是因为它会静默删除函数、undefined 和 Symbol，
 * 还会把 Date、NaN 等值改写成另一种语义。
 */
export const cloneRuntimeJsonValue = (
  value: unknown,
  path: string,
  issues: IRuntimeDocumentValidationIssue[],
  ancestors: Set<object>,
): RuntimeJsonValue | undefined => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    if (Number.isFinite(value)) return value;
    pushRuntimeIssue(issues, 'runtime-non-json-value', path, '数字必须是有限值');
    return undefined;
  }

  if (typeof value !== 'object') {
    pushRuntimeIssue(issues, 'runtime-non-json-value', path, `不支持持久化 ${typeof value} 值`);
    return undefined;
  }

  if (ancestors.has(value)) {
    pushRuntimeIssue(issues, 'runtime-non-json-value', path, 'Runtime 文档不能包含循环引用');
    return undefined;
  }

  if (Array.isArray(value)) {
    ancestors.add(value);
    const cloned: RuntimeJsonValue[] = [];
    const arrayIndexKeys = new Set<string>();
    for (let index = 0; index < value.length; index += 1) {
      const key = String(index);
      const nextPath = `${path}[${index}]`;
      arrayIndexKeys.add(key);
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (!descriptor?.enumerable || !('value' in descriptor)) {
        // JSON.parse 不会生成稀疏项或访问器；拒绝而不是让 stringify 静默改写成 null。
        pushRuntimeIssue(
          issues,
          'runtime-non-json-value',
          nextPath,
          'Runtime 数组只允许连续、可枚举的数据项',
        );
        cloned.push(null);
        continue;
      }
      cloned.push(
        cloneRuntimeJsonValue(descriptor.value, nextPath, issues, ancestors) ?? null,
      );
    }
    Reflect.ownKeys(value).forEach((key) => {
      if (key === 'length' || (typeof key === 'string' && arrayIndexKeys.has(key))) return;
      if (typeof key !== 'string') {
        pushRuntimeIssue(issues, 'runtime-non-json-value', path, 'Runtime 数组不能包含 Symbol 键');
        return;
      }
      const nextPath = `${path}.${key}`;
      if (UNSAFE_SCHEMA_OBJECT_KEYS.has(key)) {
        pushRuntimeIssue(issues, 'runtime-unsafe-key', nextPath, `Runtime 文档包含危险对象键 ${key}`);
        return;
      }
      pushRuntimeIssue(
        issues,
        'runtime-non-json-value',
        nextPath,
        'Runtime 数组不能包含会被 JSON 忽略的附加属性',
      );
    });
    ancestors.delete(value);
    return cloned;
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    pushRuntimeIssue(
      issues,
      'runtime-non-json-value',
      path,
      'Runtime 文档只允许普通 JSON 对象，不能包含 Date 或 class instance',
    );
    return undefined;
  }

  ancestors.add(value);
  const cloned: Record<string, RuntimeJsonValue> = {};
  Reflect.ownKeys(value).forEach((key) => {
    if (typeof key !== 'string') {
      pushRuntimeIssue(issues, 'runtime-non-json-value', path, 'Runtime 对象不能包含 Symbol 键');
      return;
    }

    const nextPath = `${path}.${key}`;
    if (UNSAFE_SCHEMA_OBJECT_KEYS.has(key)) {
      pushRuntimeIssue(issues, 'runtime-unsafe-key', nextPath, `Runtime 文档包含危险对象键 ${key}`);
      return;
    }

    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (!descriptor?.enumerable || !('value' in descriptor)) {
      pushRuntimeIssue(
        issues,
        'runtime-non-json-value',
        nextPath,
        'Runtime 对象只允许可枚举的数据属性',
      );
      return;
    }

    const clonedValue = cloneRuntimeJsonValue(descriptor.value, nextPath, issues, ancestors);
    if (clonedValue !== undefined) {
      cloned[key] = clonedValue;
    }
  });
  ancestors.delete(value);
  return cloned;
};

/** 校验对象是否只包含冻结合同允许的字段。 */
export const validateAllowedKeys = (
  value: Record<string, unknown>,
  allowedKeys: Set<string>,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
  code: RuntimeBlockingErrorCode,
) => {
  Object.keys(value).forEach((key) => {
    if (!allowedKeys.has(key)) {
      pushRuntimeIssue(issues, code, `${path}.${key}`, `字段 ${key} 不在 Runtime v1 allowlist 中`);
    }
  });
};

export const RUNTIME_EXPRESSION_PATTERN = /^\s*\{\{([\s\S]*)\}\}\s*$/;

/** 递归拒绝请求 URL、参数和数据中的 Formily 表达式，避免文档把 Store 数据拼入宿主请求。 */
export const validateRuntimeRequestExpressionFreeValue = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (typeof value === 'string') {
    if (RUNTIME_EXPRESSION_PATTERN.test(value)) {
      pushRuntimeIssue(
        issues,
        'runtime-unsafe-request-config',
        path,
        'Runtime 持久化请求配置不允许使用数据表达式',
      );
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      validateRuntimeRequestExpressionFreeValue(item, issues, `${path}[${index}]`);
    });
    return;
  }
  if (!isObjectRecord(value)) return;
  Object.entries(value).forEach(([key, child]) => {
    validateRuntimeRequestExpressionFreeValue(child, issues, `${path}.${key}`);
  });
};

/** 校验所有持久化请求都只含 URL、method、params 和 data。 */
export const validateRuntimeRequestConfig = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (!isObjectRecord(value)) {
    pushRuntimeIssue(issues, 'runtime-unsafe-request-config', path, '请求配置必须是对象');
    return;
  }

  validateAllowedKeys(
    value,
    RUNTIME_REQUEST_KEYS,
    issues,
    path,
    'runtime-unsafe-request-config',
  );
  validateRuntimeRequestExpressionFreeValue(value, issues, path);
  if (
    typeof value.url !== 'string'
    || !value.url.trim()
    || value.url.trim() !== value.url
  ) {
    pushRuntimeIssue(
      issues,
      'runtime-unsafe-request-config',
      `${path}.url`,
      '请求配置 url 必须是非空且不含首尾空白的字符串',
    );
  }
  if (
    value.method !== undefined
    && (
      typeof value.method !== 'string'
      || !value.method.trim()
      || value.method.trim() !== value.method
    )
  ) {
    pushRuntimeIssue(
      issues,
      'runtime-unsafe-request-config',
      `${path}.method`,
      '请求配置 method 必须是非空且不含首尾空白的字符串',
    );
  }
  (['params', 'data'] as const).forEach((propertyName) => {
    if (
      value[propertyName] !== undefined
      && !isObjectRecord(value[propertyName])
    ) {
      pushRuntimeIssue(
        issues,
        'runtime-unsafe-request-config',
        `${path}.${propertyName}`,
        `请求配置 ${propertyName} 必须是 JSON 对象`,
      );
    }
  });
};

/** 校验字典 self/by 模式与 byField 的一致性。 */
export const validateRuntimeDictMode = (
  value: Record<string, unknown>,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (value.type !== undefined && value.type !== 'self' && value.type !== 'by') {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.type`,
      'dictConfig.type 只允许 self 或 by',
    );
  }

  const byFields = Array.isArray(value.byField) ? value.byField : [value.byField];
  const hasInvalidByField = value.byField !== undefined
    && (
      !byFields.length
      || byFields.some(item => (
        typeof item !== 'string'
        || !item.trim()
        || item.trim() !== item
      ))
    );
  if (hasInvalidByField || (value.type === 'by' && value.byField === undefined)) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.byField`,
      value.type === 'by'
        ? 'by 模式必须提供非空 byField'
        : 'byField 必须是非空字符串或非空字符串数组',
    );
  }
};

/** 校验字典布尔开关、映射键和子项键等辅助字段。 */
export const validateRuntimeDictOptions = (
  value: Record<string, unknown>,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (value.toMap !== undefined && typeof value.toMap !== 'boolean') {
    pushRuntimeIssue(issues, 'invalid-runtime-document', `${path}.toMap`, 'toMap 必须是布尔值');
  }
  if (value.toOptions !== undefined && typeof value.toOptions !== 'boolean') {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.toOptions`,
      'toOptions 必须是布尔值',
    );
  }
  /** 校验字典字段映射的每个键和值，避免下游把非字符串当属性名读取。 */
  const validateStringMapping = (mapping: Record<string, unknown>, mappingPath: string) => {
    Object.entries(mapping).forEach(([key, item]) => {
      if (
        !key.trim()
        || key.trim() !== key
        || UNSAFE_SCHEMA_OBJECT_KEYS.has(key)
        || typeof item !== 'string'
        || !item.trim()
        || item.trim() !== item
      ) {
        pushRuntimeIssue(
          issues,
          'invalid-runtime-document',
          `${mappingPath}.${key}`,
          '字段映射的键和值必须是不含首尾空白的安全非空字符串',
        );
      }
    });
  };
  if (value.keys !== undefined) {
    if (!isObjectRecord(value.keys)) {
      pushRuntimeIssue(issues, 'invalid-runtime-document', `${path}.keys`, 'keys 必须是对象');
    } else {
      validateStringMapping(value.keys, `${path}.keys`);
    }
  }
  if (
    value.paramKeys !== undefined
    && typeof value.paramKeys !== 'string'
    && !isObjectRecord(value.paramKeys)
  ) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.paramKeys`,
      'paramKeys 必须是字符串或对象',
    );
  } else if (typeof value.paramKeys === 'string') {
    if (!value.paramKeys.trim() || value.paramKeys.trim() !== value.paramKeys) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.paramKeys`,
        'paramKeys 字符串必须是不含首尾空白的非空值',
      );
    }
  } else if (isObjectRecord(value.paramKeys)) {
    validateStringMapping(value.paramKeys, `${path}.paramKeys`);
  }
  if (value.childrenKey !== undefined && typeof value.childrenKey !== 'string') {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.childrenKey`,
      'childrenKey 必须是字符串',
    );
  }
};

/** 校验 Store 字典声明及其可选请求配置。 */
export const validateRuntimeDictConfig = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (!isObjectRecord(value)) {
    pushRuntimeIssue(issues, 'invalid-runtime-document', path, 'dictConfig 条目必须是对象');
    return;
  }

  validateAllowedKeys(value, RUNTIME_DICT_KEYS, issues, path, 'invalid-runtime-document');
  if (
    typeof value.field !== 'string'
    || !value.field.trim()
    || value.field.trim() !== value.field
  ) {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.field`,
      'dictConfig.field 必须是不含首尾空白的非空字符串',
    );
  }
  validateRuntimeDictMode(value, issues, path);
  validateRuntimeDictOptions(value, issues, path);
  if (value.api !== undefined) {
    validateRuntimeRequestConfig(value.api, issues, `${path}.api`);
  }
};

/** 校验 Runtime v1 的 Store 顶层 allowlist 和字段形状。 */
export const validateRuntimeStoreConfig = (
  value: unknown,
  issues: IRuntimeDocumentValidationIssue[],
  path: string,
) => {
  if (!isObjectRecord(value)) {
    pushRuntimeIssue(issues, 'invalid-runtime-document', path, 'store 必须是对象');
    return;
  }

  validateAllowedKeys(value, RUNTIME_STORE_KEYS, issues, path, 'invalid-runtime-document');
  if (value.type !== undefined && value.type !== 'base' && value.type !== 'list') {
    pushRuntimeIssue(
      issues,
      'invalid-runtime-document',
      `${path}.type`,
      'store.type 只允许 base 或 list',
    );
  }
  [
    'defaultValues',
    'extInfo',
    'fieldsConfig',
    'options',
  ].forEach((key) => {
    if (value[key] !== undefined && !isObjectRecord(value[key])) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.${key}`,
        `store.${key} 必须是对象`,
      );
    }
  });
  if (value.dictConfig !== undefined) {
    if (!Array.isArray(value.dictConfig)) {
      pushRuntimeIssue(
        issues,
        'invalid-runtime-document',
        `${path}.dictConfig`,
        'store.dictConfig 必须是数组',
      );
    } else {
      value.dictConfig.forEach((item, index) => {
        validateRuntimeDictConfig(item, issues, `${path}.dictConfig[${index}]`);
      });
    }
  }
  if (value.api !== undefined) {
    validateRuntimeRequestConfig(value.api, issues, `${path}.api`);
  }
};
